import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';
import { DatapointStatus, type TraceErrorObject } from 'owa-analytics-types';
import { logStartGreyError, LogStartPerformanceUsage } from 'owa-analytics-start';
import {
    setIsDataWorkerFaulted,
    isGulpOrBranching,
    setIsDataWorkerNotSupported,
    setDataWorkerInitError,
} from 'owa-config';
import { isDataWorkerPlatformNotSupportedWithReason } from 'owa-data-worker-check/lib/isDataWorkerPlatformSupported';
import type { SyncEvent } from 'owa-offline-sync-diagnostics';
import { recordSyncEvents } from 'owa-offline-sync-diagnostics-client';
import { debugErrorThatWillShowErrorPopupOnly, errorThatWillCauseAlert } from 'owa-trace';
import OwaWorker from 'owa-worker-shared/lib/Worker';
import { isCriticalWorkerError } from 'owa-worker-shared/lib/utils/isCriticalWorkerError';

let worker: Worker | undefined;
let invalidWorkerError: TraceErrorObject | undefined;

const WorkerPrefix = 'DataWorker';

interface WorkerErrorEvent extends ErrorEvent {
    name: string;
    stack?: string;
}

let shouldLogDatapoint = true;

export function loadDataWorker(): Worker | undefined {
    if (invalidWorkerError) {
        // throw a meaningful error instead of returning a broken object
        var newError = new Error('Invalid Worker Object') as TraceErrorObject;
        newError.additionalInfo = invalidWorkerError.additionalInfo;
        throw newError;
    } else if (!worker) {
        let dp: LogStartPerformanceUsage | undefined;
        if (shouldLogDatapoint) {
            shouldLogDatapoint = false;
            dp = new LogStartPerformanceUsage(
                'DataWorkerInit',
                INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT
            );
        }

        const reason = isDataWorkerPlatformNotSupportedWithReason();
        if (reason) {
            dp?.addCustomProperty('NotSupportedReason', reason);
            dp?.endWithError(DatapointStatus.ClientError);
            setIsDataWorkerNotSupported();
        } else {
            try {
                worker = new OwaWorker('owa.worker.data.js', {
                    name: 'DataWorker',
                }) as Worker;

                validateWorkerObject();
                worker.onerror = onError;
                dp?.end();
            } catch (e) {
                setDataWorkerInitError(e as Error);
                dp?.endWithError(DatapointStatus.ClientError, e as Error);
                throw e;
            }
        }
    }

    return worker;
}

function validateWorkerObject() {
    if (typeof worker?.addEventListener !== 'function') {
        // seeing errors indicating this property is missing.  enumerate all properties including prototype properties
        // into a set and also get the constructor type of the object returned then log a gray error with the
        // diagnostics
        invalidWorkerError = new Error('Invalid Worker Object') as TraceErrorObject;

        // Get comprehensive property information
        const allProps = new Set<string>();
        let current = worker;
        let sanity = 10;
        const prototypeChain: string[] = [];

        while (current && sanity-- > 0) {
            prototypeChain.push(current.constructor?.name || 'Unknown');
            Object.getOwnPropertyNames(current).forEach(prop => allProps.add(prop));
            Object.getOwnPropertySymbols(current).forEach(sym => allProps.add(sym.toString()));
            current = Object.getPrototypeOf(current);
            if (current === Object.prototype) {
                break;
            }
        }

        invalidWorkerError.additionalInfo = {
            allProperties: Array.from(allProps).sort(),
            prototypeChain,
            constructor: worker?.constructor?.name,
            hasPostMessage: typeof worker?.postMessage,
            hasTerminate: typeof worker?.terminate,
            hasOnMessage: typeof worker?.onmessage,
            hasAddEventListener: typeof worker?.addEventListener,
            objectType: Object.prototype.toString.call(worker),
            isInstanceOfWorker: worker instanceof Worker,
        };

        throw invalidWorkerError;
    }
}

function onError(ev: ErrorEvent) {
    if (isCriticalWorkerError(ev)) {
        setIsDataWorkerFaulted(ev as any);
        logStartGreyError(
            'WorkerInitError',
            // eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- This is infrastructure telemetry about loading the worker. The global account is not available yet
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            ev.error
        );
        const syncEvent: SyncEvent = {
            type: 'EngineStateChanged',
            data: ['Worker Fault: ' + ev.message],
        };
        recordSyncEvents([syncEvent]);

        // popup error for devs and branch users to make worker breaks more obvious
        // there can be hash at the end of the script name
        if (isGulpOrBranching() && ev.message?.includes('owa.worker.data.')) {
            debugErrorThatWillShowErrorPopupOnly(
                'DataWorker entry script failed to execute. Offline features will not be available.  Check console errors for more details.'
            );
        }
    } else {
        const workerError = ev as WorkerErrorEvent;
        workerError.name = WorkerPrefix;
        let message = workerError.message;
        const newlineIndex = message?.indexOf('\n');
        if (newlineIndex > 0) {
            workerError.stack = message.substring(newlineIndex + 1);
            message = message.substring(0, newlineIndex);
        }
        /* eslint-disable-next-line owa-custom-rules/no-error-dynamic-event-names -- (https://aka.ms/OWALintWiki)
         * We are just passing the on error through so we want to keep this dynamic so we don't group the
         * different errors together. */
        errorThatWillCauseAlert(WorkerPrefix + ':' + message, workerError.error ?? workerError);
    }
}

export function testReset(): void {
    invalidWorkerError = undefined;
    worker = undefined;
}
